import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Drawer } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { FunctionComponent, PropsWithChildren } from 'react';

export interface SidePanelProps extends PropsWithChildren {
    showCloseButton?: boolean;
    side: 'left' | 'right';
    width?: number;
    zIndex?: number;
    hide?: boolean;
    onClose: () => void;
    className?: string;
    classesInner?: {
        root?: string;
        paper?: string;
    };
    classesBackdrop?: {
        classes?: {
            root?: string;
        };
    };
    hideBackdrop?: boolean;
    // Opt-in: stop MUI Drawer from trapping focus, so modals rendered via a
    // document.body portal (e.g. GenericModal) stay editable. Off by default.
    disableEnforceFocus?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            'zIndex': 999,
            '& .MuiDrawer-paperAnchorRight': {
                width: '100%',
                [theme.breakpoints.up('lg')]: {
                    width: (props: SidePanelProps) => `${props.width ?? 590}px !important`
                }
            }
        },
        closeButtonContainer: {
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            [theme.breakpoints.down('md')]: {
                display: 'none'
            }
        },
        closeButton: (props: SidePanelProps) => ({
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#000000',
            borderRadius: '50%',
            top: 24,
            right: props.side === 'right' ? (props.width ?? 590) + 30 : undefined,
            left: props.side === 'left' ? (props.width ?? 590) + 30 : undefined,
            position: 'fixed',
            cursor: 'pointer',
            height: 46,
            width: 46
        }),
        closeIcon: {
            color: '#FFFFFF',
            height: 20,
            width: 20
        }
    })
);

export const SidePanel: FunctionComponent<SidePanelProps> = props => {
    const paperRef = React.useRef<HTMLDivElement>(null);
    const classes = useStyles(props);

    React.useEffect(() => {
        if (paperRef.current) {
            paperRef.current.removeAttribute('tabindex');
        }

        return () => {
            if (paperRef.current) {
                paperRef.current.removeAttribute('tabindex');
            }
        };
    }, [paperRef.current]);

    return (
        <Drawer
            onClose={props.onClose}
            open={!(props.hide ?? false)}
            anchor={props.side}
            className={clsx(classes.container, props.className)}
            classes={{
                ...props.classesInner,
                paper: props.classesInner?.paper
            }}
            PaperProps={{
                ref: paperRef
            }}
            BackdropProps={{
                invisible: props.hideBackdrop,
                classes: {
                    root: props.classesBackdrop?.classes?.root
                }
            }}
            ModalProps={{
                disableEnforceFocus: props.disableEnforceFocus
            }}
            style={{
                zIndex: props.zIndex // custom zIndex
            }}
        >
            {props.showCloseButton && (
                <Box className={classes.closeButtonContainer}>
                    <Box
                        className={clsx(classes.closeButton, 'sidepanel-close-button')}
                        onClick={props.onClose}
                    >
                        <FontAwesomeIcon
                            icon={faXmark}
                            className={classes.closeIcon}
                        />
                    </Box>
                </Box>
            )}
            {props.children}
        </Drawer>
    );
};

export default SidePanel;
